import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../shared/api';
import { Input, Label } from './field';

export interface CustomerOption {
  readonly id: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly phone?: string;
  readonly email?: string;
}

interface CustomerSearchResult {
  readonly customers: CustomerOption[];
  readonly dogNamesByCustomerId: Record<string, string[]>;
}

export const SEARCH_DEBOUNCE_MS = 300;

export function useDebouncedValue<T>(value: T, delay = SEARCH_DEBOUNCE_MS) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timeout);
  }, [delay, value]);

  return debouncedValue;
}

export function SearchBox({
  value,
  onChange,
  placeholder = 'Buscar',
  label = 'Buscar',
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly label?: string;
}) {
  const [inputValue, setInputValue] = useState(value);
  const debouncedValue = useDebouncedValue(inputValue);
  const inputId = `search-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (debouncedValue !== value) onChange(debouncedValue);
  }, [debouncedValue, onChange, value]);

  return (
    <div className="group relative w-full rounded-md border border-[#d6ded0] bg-white shadow-sm transition duration-200 focus-within:border-[#75816a] focus-within:ring-2 focus-within:ring-[#dfe8d8] sm:max-w-md">
      <Label className="sr-only" htmlFor={inputId}>{label}</Label>
      <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-[#75816a] transition-transform duration-200 group-focus-within:scale-110" aria-hidden="true" />
      <Input id={inputId} className="h-10 w-full border-0 bg-transparent pl-9 pr-10 shadow-none focus:border-0 focus:ring-0" value={inputValue} placeholder={placeholder} onChange={(event) => setInputValue(event.target.value)} />
      {inputValue ? (
        <button type="button" className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-md text-[#75816a] transition-colors hover:bg-[#eef3ea] hover:text-[#334033]" aria-label={`Limpiar ${label.toLowerCase()}`} onClick={() => setInputValue('')}>
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

export function CustomerSearchBox({
  id = 'customerId',
  customers = [],
  value,
  onChange,
  disabled,
  required,
  placeholder = 'Buscar cliente',
  minSearchLength = 2,
}: {
  readonly id?: string;
  readonly customers?: CustomerOption[];
  readonly value: string;
  readonly onChange: (customerId: string) => void;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly placeholder?: string;
  readonly minSearchLength?: number;
}) {
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebouncedValue(inputValue);
  const lastSelectedLabel = useRef('');
  const shouldSearch = !value && debouncedInput.trim().length >= minSearchLength;
  const selectedCustomerQuery = useQuery({
    queryKey: ['customer', value],
    queryFn: () => api.customers.get(value),
    enabled: Boolean(value) && !customers.some((customer) => customer.id === value),
  });
  const customersQuery = useQuery({
    queryKey: ['customer-search', debouncedInput.trim()],
    queryFn: async (): Promise<CustomerSearchResult> => {
      const query = debouncedInput.trim();
      const [customerResult, dogResult] = await Promise.all([
        api.customers.list({ search: query, limit: 10 }),
        api.dogs.list({ q: query, limit: 25 }),
      ]);
      const matchingDogs = dogResult.items.filter((dog) => normalizeSearch(dog.name).includes(normalizeSearch(query)));
      const customerById = new Map(customerResult.items.map((customer) => [customer.id, customer]));
      const missingCustomerIds = [...new Set(matchingDogs.map((dog) => dog.customerId))]
        .filter((customerId) => !customerById.has(customerId));
      const dogCustomers = await Promise.all(missingCustomerIds.map((customerId) => api.customers.get(customerId)));
      for (const customer of dogCustomers) customerById.set(customer.id, customer);

      const dogNamesByCustomerId: Record<string, string[]> = {};
      for (const dog of matchingDogs) {
        const names = dogNamesByCustomerId[dog.customerId] ?? [];
        if (!names.includes(dog.name)) dogNamesByCustomerId[dog.customerId] = [...names, dog.name];
      }
      return { customers: [...customerById.values()], dogNamesByCustomerId };
    },
    enabled: shouldSearch && !disabled,
  });
  const customerOptions = useMemo(() => {
    const byId = new Map<string, CustomerOption>();
    const query = normalizeSearch(debouncedInput);
    for (const customer of customers) {
      if (!query || normalizeSearch(customerLabel(customer)).includes(query)) byId.set(customer.id, customer);
    }
    if (selectedCustomerQuery.data) byId.set(selectedCustomerQuery.data.id, selectedCustomerQuery.data);
    for (const customer of customersQuery.data?.customers ?? []) byId.set(customer.id, customer);
    return [...byId.values()];
  }, [customers, customersQuery.data?.customers, debouncedInput, selectedCustomerQuery.data]);
  const selectedCustomer = customerOptions.find((customer) => customer.id === value);
  const showResults = !value && !disabled && inputValue.trim().length >= minSearchLength && customerOptions.length > 0;

  useEffect(() => {
    if (selectedCustomer) {
      const label = customerLabel(selectedCustomer);
      lastSelectedLabel.current = label;
      setInputValue(label);
      return;
    }
    if (!value && inputValue === lastSelectedLabel.current) setInputValue('');
  }, [selectedCustomer?.id]);

  return (
    <div className="grid gap-1">
      <div className="group relative rounded-md border border-[#d6ded0] bg-white shadow-sm transition duration-200 focus-within:border-[#75816a] focus-within:ring-2 focus-within:ring-[#dfe8d8]">
        <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-[#75816a] transition-transform duration-200 group-focus-within:scale-110" aria-hidden="true" />
        <Input
          id={id}
          className={value || inputValue ? 'h-10 w-full border-0 bg-transparent pl-9 pr-12 shadow-none focus:border-0 focus:ring-0' : 'h-10 w-full border-0 bg-transparent pl-9 pr-3 shadow-none focus:border-0 focus:ring-0'}
          value={inputValue}
          required={required}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder={placeholder}
          onChange={(event) => {
            const nextValue = event.target.value;
            setInputValue(nextValue);
            if (!nextValue || value) onChange('');
          }}
        />
        {value || inputValue ? (
          <button
            type="button"
            className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#d6ded0] bg-white text-[#4f5d49] shadow-sm transition-colors hover:bg-[#eef3ea] hover:text-[#334033]"
            aria-label="Limpiar cliente"
            title="Limpiar cliente"
            disabled={disabled}
            onClick={() => {
              lastSelectedLabel.current = '';
              setInputValue('');
              onChange('');
            }}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {!value && !disabled && inputValue.trim().length > 0 && inputValue.trim().length < minSearchLength ? <p className="text-xs text-[#687461]">Escribe al menos {minSearchLength} caracteres para buscar.</p> : null}
      {!value && customersQuery.isFetching ? <p className="text-xs text-[#687461]">Buscando clientes...</p> : null}
      {!value && shouldSearch && !customersQuery.isFetching && customerOptions.length === 0 ? <p className="text-xs text-[#687461]">No se encontraron clientes.</p> : null}
      {showResults ? (
        <div className="max-h-56 overflow-y-auto rounded-md border border-[#d6ded0] bg-white shadow-sm">
          {customerOptions.map((customer) => (
            <button
              key={customer.id}
              type="button"
              className="block w-full border-b border-[#eef3ea] px-3 py-2 text-left text-sm text-[#334033] transition-colors duration-150 last:border-b-0 hover:bg-[#eef3ea]"
              onClick={() => {
                const label = customerLabel(customer);
                lastSelectedLabel.current = label;
                setInputValue(label);
                onChange(customer.id);
              }}
            >
              <span className="block font-medium">{customer.firstName} {customer.lastName}</span>
              {customersQuery.data?.dogNamesByCustomerId[customer.id]?.length ? (
                <span className="block text-xs text-[#2f6b38]">Perro: {customersQuery.data.dogNamesByCustomerId[customer.id].join(', ')}</span>
              ) : null}
              {customer.phone || customer.email ? <span className="block text-xs text-[#687461]">{customer.phone ?? customer.email}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function customerLabel(customer: CustomerOption) {
  const name = `${customer.firstName} ${customer.lastName}`;
  const detail = customer.phone ?? customer.email;
  return detail ? `${name} · ${detail}` : name;
}

function normalizeSearch(value: unknown) {
  return String(value ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
}
